// Ren logik för "Passar den hos mig?" — inga beroenden, testbar och delad
// mellan SVG-planen och AR-vyn.

export type BodyType = "integrerad" | "halvintegrerad" | "alkov" | "husvagn" | "van";

export type VehicleDims = {
  /** meter */
  length: number;
  /** meter, inkl. backspeglar när de är utfällda */
  width: number;
  /** meter, inkl. takluckor/AC */
  height: number;
  /** meter, markisens utfällning */
  awning: number;
  bodyType: BodyType;
};

/**
 * Typiska mått från verkstaden i Kalix. Används när annonsen bara anger längd.
 * Bredd anges som karossbredd; speglarna läggs på separat i mirrorWidth().
 */
export const BODY_DEFAULTS: Record<BodyType, Omit<VehicleDims, "length" | "bodyType">> = {
  integrerad: { width: 2.32, height: 2.95, awning: 2.5 },
  halvintegrerad: { width: 2.3, height: 2.85, awning: 2.5 },
  alkov: { width: 2.3, height: 3.1, awning: 2.5 },
  van: { width: 2.05, height: 2.6, awning: 2.2 },
  husvagn: { width: 2.3, height: 2.6, awning: 2.5 },
};

export const BODY_LABEL: Record<BodyType, string> = {
  integrerad: "Integrerad husbil",
  halvintegrerad: "Halvintegrerad husbil",
  alkov: "Alkovhusbil",
  van: "Van / campervan",
  husvagn: "Husvagn",
};

/** Utfällda backspeglar lägger på ca 30 cm per sida på en husbil. */
export function mirrorWidth(d: VehicleDims): number {
  return d.bodyType === "husvagn" ? d.width : d.width + 0.6;
}

export function guessBodyType(title: string, category?: string | null): BodyType {
  const t = `${title} ${category ?? ""}`.toLowerCase();
  if (/husvagn|caravan/.test(t)) return "husvagn";
  if (/alkov|overcab|profil.*alkov/.test(t)) return "alkov";
  if (/integrerad(?!.*halv)|i-?modell|liner/.test(t) && !/halv/.test(t)) return "integrerad";
  if (/van\b|camper|kastenwagen|buscamper|possl|twin/.test(t)) return "van";
  return "halvintegrerad";
}

export function dimsFor(
  title: string,
  lengthMeters: number | null,
  category?: string | null,
): VehicleDims {
  const bodyType = guessBodyType(title, category);
  const base = BODY_DEFAULTS[bodyType];
  const fallbackLength = bodyType === "van" ? 5.99 : bodyType === "husvagn" ? 7.2 : 7.4;
  return {
    bodyType,
    length: lengthMeters && lengthMeters > 3 && lengthMeters < 12 ? lengthMeters : fallbackLength,
    ...base,
  };
}

export type Site = {
  /** uppfartens bredd i meter */
  driveWidth: number;
  /** tillgänglig längd i meter */
  driveLength: number;
  /** fri höjd, t.ex. portöppning eller grenar (0 = fri himmel) */
  clearHeight: number;
  /** grindens/portens bredd (0 = ingen grind) */
  gateWidth: number;
  /** gatans fria bredd framför infarten i meter – används i svängsimuleringen */
  streetWidth?: number;
  /** infartens bredd där den ansluter mot gatan (0 = samma som uppfartens bredd) */
  entryWidth?: number;
};

export const DEFAULT_SITE: Site = {
  driveWidth: 3.5,
  driveLength: 9,
  clearHeight: 0,
  gateWidth: 0,
  streetWidth: 6,
  entryWidth: 0,
};

export type CheckLevel = "ok" | "warn" | "fail";

export type Check = {
  id: string;
  label: string;
  level: CheckLevel;
  detail: string;
  /** Marginal i meter, negativ = det fattas plats. */
  margin: number;
};

const m = (v: number) => `${v.toFixed(2).replace(".", ",")} m`;
const cm = (v: number) => `${Math.round(v * 100)} cm`;

/**
 * Praxis från leveranser och kundbesök: 60 cm gångmån längs långsidan,
 * 50 cm bakom för att kunna öppna bakluckan, 20 cm marginal i portöppning.
 */
export const MARGINS = {
  walkSide: 0.6,
  rearAccess: 0.5,
  heightSafety: 0.2,
  gateSafety: 0.15,
  doorSwing: 0.75,
  snow: 0.3,
};

export function evaluateFit(d: VehicleDims, site: Site): Check[] {
  const checks: Check[] = [];
  const w = mirrorWidth(d);

  // Längd
  const needLength = d.length + MARGINS.rearAccess;
  const lengthMargin = site.driveLength - needLength;
  checks.push({
    id: "length",
    label: "Längd på uppfarten",
    level: lengthMargin >= 1 ? "ok" : lengthMargin >= 0 ? "warn" : "fail",
    margin: lengthMargin,
    detail:
      lengthMargin >= 0
        ? `Fordonet är ${m(d.length)} och du har ${m(site.driveLength)}. Kvar bakom: ${cm(lengthMargin + MARGINS.rearAccess)} — räcker för att öppna bakluckan.`
        : `Det fattas ${cm(Math.abs(lengthMargin))}. Fordonet är ${m(d.length)} och du behöver ${m(needLength)} med utrymme bakom.`,
  });

  // Bredd med gångmån
  const needWidth = w + MARGINS.walkSide;
  const widthMargin = site.driveWidth - needWidth;
  checks.push({
    id: "width",
    label: "Bredd + gångmån",
    level: widthMargin >= 0.4 ? "ok" : widthMargin >= 0 ? "warn" : "fail",
    margin: widthMargin,
    detail:
      widthMargin >= 0
        ? `Med utfällda speglar är den ${m(w)} bred. Du har ${m(site.driveWidth)} — ${cm(site.driveWidth - w)} kvar att gå på.`
        : `Med speglar ute är den ${m(w)}. Du behöver minst ${m(needWidth)} för att komma förbi längs långsidan.`,
  });

  // Dörrsvep + markis
  const awningMargin = site.driveWidth - w - MARGINS.doorSwing;
  checks.push({
    id: "door",
    label: "Dörr & markis",
    level: awningMargin >= 0 ? "ok" : "warn",
    margin: awningMargin,
    detail:
      awningMargin >= 0
        ? `Entrédörren svänger ut ca 75 cm och får plats. Markisen tar ${m(d.awning)} — fäll den mot fri sida.`
        : `Dörren behöver ca 75 cm fritt utanför karossen. Här blir det trångt — vänd fordonet så dörrsidan hamnar mot den öppna sidan.`,
  });

  // Höjd
  if (site.clearHeight > 0) {
    const heightMargin = site.clearHeight - d.height - MARGINS.heightSafety;
    checks.push({
      id: "height",
      label: "Fri höjd",
      level: heightMargin >= 0.15 ? "ok" : heightMargin >= 0 ? "warn" : "fail",
      margin: heightMargin,
      detail:
        heightMargin >= 0
          ? `Total höjd ${m(d.height)} inkl. takluckor och AC. Med ${m(site.clearHeight)} fri höjd har du ${cm(site.clearHeight - d.height)} luft.`
          : `Total höjd ${m(d.height)} inkl. takutrustning — det fattas ${cm(Math.abs(heightMargin))}. Mät om vid porten innan du kör in.`,
    });
  }

  // Grind
  if (site.gateWidth > 0) {
    const gateMargin = site.gateWidth - d.width - MARGINS.gateSafety;
    checks.push({
      id: "gate",
      label: "Grind / portöppning",
      level: gateMargin >= 0.25 ? "ok" : gateMargin >= 0 ? "warn" : "fail",
      margin: gateMargin,
      detail:
        gateMargin >= 0
          ? `Karossen är ${m(d.width)} — du fäller in speglarna och har ${cm(site.gateWidth - d.width)} kvar.`
          : `Karossen är ${m(d.width)} och öppningen ${m(site.gateWidth)}. Det går inte utan att bredda öppningen.`,
    });
  }

  // Vinter
  const snowMargin = site.driveWidth - w - MARGINS.snow;
  checks.push({
    id: "snow",
    label: "Vinter i Norrbotten",
    level: snowMargin >= 0 ? "ok" : "warn",
    margin: snowMargin,
    detail:
      snowMargin >= 0
        ? `Snövallar äter 20–40 cm per sida i februari. Du har marginal kvar när plogen varit där.`
        : `När snövallen växer 30 cm försvinner din marginal. Planera för snöröjning eller vinterförvaring.`,
  });

  return checks;
}

export function verdict(checks: Check[]): { level: CheckLevel; text: string } {
  if (checks.some((c) => c.level === "fail"))
    return { level: "fail", text: "Passar inte som uppfarten ser ut i dag" };
  if (checks.some((c) => c.level === "warn"))
    return { level: "warn", text: "Går att lösa – men marginalerna är små" };
  return { level: "ok", text: "Passar med god marginal" };
}

/**
 * Hur stor ser ett objekt ut på skärmen? Används av AR-vyn.
 * Antar ca 65° horisontell bildvinkel, vilket motsvarar en typisk
 * mobilkameras huvudlins (~26 mm ekvivalent).
 */
export const CAMERA_HFOV_DEG = 65;

export function projectedPixels(
  sizeMeters: number,
  distanceMeters: number,
  viewportPx: number,
): number {
  const halfFov = (CAMERA_HFOV_DEG * Math.PI) / 180 / 2;
  const visibleWidth = 2 * distanceMeters * Math.tan(halfFov);
  return (sizeMeters / visibleWidth) * viewportPx;
}

/** Samma projektion men med kalibrerad bildvinkel. */
export function projectedPixelsFov(
  sizeMeters: number,
  distanceMeters: number,
  viewportPx: number,
  hfovDeg: number,
): number {
  const halfFov = (hfovDeg * Math.PI) / 180 / 2;
  const visibleWidth = 2 * Math.max(0.5, distanceMeters) * Math.tan(halfFov);
  return (sizeMeters / visibleWidth) * viewportPx;
}

/** Vertikal bildvinkel ur horisontell + bildförhållande. */
export function vfovFromHfov(hfovDeg: number, widthPx: number, heightPx: number): number {
  const halfH = (hfovDeg * Math.PI) / 180 / 2;
  const halfV = Math.atan(Math.tan(halfH) * (heightPx / Math.max(1, widthPx)));
  return (halfV * 360) / Math.PI;
}

/**
 * Kalibrering: användaren matchar en linje mot ett känt mått (garageport, bilens
 * längd, tomtgräns) på ett känt avstånd. Ur det faller kamerans faktiska
 * bildvinkel ut — betydligt exaktare än att gissa 65°.
 */
export function fovFromReference(
  refMeters: number,
  refDistanceMeters: number,
  refPx: number,
  viewportPx: number,
): number {
  const visibleWidth = (refMeters * viewportPx) / Math.max(1, refPx);
  const half = Math.atan(visibleWidth / (2 * Math.max(0.5, refDistanceMeters)));
  const deg = (half * 360) / Math.PI;
  return Math.min(110, Math.max(40, deg));
}

/**
 * Var träffar marken skärmen? Horisonten flyttas när telefonen lutas
 * (beta≈90° = telefonen upprätt, kameraaxeln vågrät).
 */
export function groundLineRatio(
  distanceMeters: number,
  cameraHeightMeters: number,
  pitchDownDeg: number,
  vfovDeg: number,
): number {
  const halfV = Math.tan((vfovDeg * Math.PI) / 180 / 2);
  const horizon = 0.5 + Math.tan((pitchDownDeg * Math.PI) / 180) / (2 * halfV);
  const below = Math.atan(cameraHeightMeters / Math.max(0.5, distanceMeters));
  return horizon + Math.tan(below) / (2 * halfV);
}
